import React, { useState, useRef, useEffect } from 'react';
import ExamAttempt from '../component/ExamComponent2/ExamAttempt';
import ExamSetup from '../component/ExamComponent2/ExamSetup';
import ExamInstructions from '../component/ExamComponent2/ExamInstructions';
import AgoraRTC from 'agora-rtc-sdk-ng';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { url } from '../helper/url_helper';
import { toast } from 'react-toastify';

// NOTE: This file unifies desktop and mobile flows for exam proctoring
// - Permission prompts are triggered by user gesture (Next/Start button)
// - Mobile uses navigator.mediaDevices.getUserMedia() with custom Agora tracks
// - Desktop uses Agora's createCameraVideoTrack/createMicrophoneAudioTrack
// - Screen share is completely DISABLED for both mobile and desktop

const APP_ID = '04e8b2a241274918a6e3a365cf5252c4';

const StartExam = () => {
  const [step, setStep] = useState(1);
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agoraError, setAgoraError] = useState('');
  const [attempt, setAttempt] = useState(null);
  const { examId } = useParams();
  const navigate = useNavigate();
  const [proctoringStatus, setProctoringStatus] = useState({
    camera: false,
    microphone: false,
    warnings: 0,
  });

  const [isMobile, setIsMobile] = useState(false);

  const clientRef = useRef(null);
  const localTracksRef = useRef({ video: null, audio: null });
  const cameraPreviewRef = useRef(null);

  const [isFetching, setIsFetching] = useState(false);

  // Detect mobile by width + userAgent
  useEffect(() => {
    const checkMobile = () => {
      const ua = navigator.userAgent || '';
      const mobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
      setIsMobile(window.innerWidth < 768 || mobileUA);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch exam details
  useEffect(() => {
    if (isFetching) return;
    const fetchExam = async () => {
      setIsFetching(true);
      try {
        const studentId = JSON.parse(localStorage.getItem('student'))?.id;
        const response = await axios.post(
          `${url}startExam/${examId}`, 
          {}, 
          { headers: { 'X-User-ID': studentId } }
        );

        if (response.data.success) {
          setAttempt(response.data.result);
          setExam(response.data.result.exam);
        } else {
          if (!response.data.message.includes("could not execute statement [Deadlock found when trying to get")) {
            toast.error(response.data.message);
          }
          if (
            response.data.message.includes("already been submitted") || 
            response.data.message.includes("time has already ended")
          ) {
            navigate('/myExams');
          }
        }
        setLoading(false);
      } catch (error) {
        console.error('Failed to start exam:', error);
        toast.error('Failed to start the exam. Please try again later.');
        navigate('/myExams');
        setLoading(false);
      } finally {
        setIsFetching(false);
      }
    };

    fetchExam();
  }, [examId, navigate]);

  // Permission check helper
  const checkPermissions = async () => {
    try {
      const cameraPerm = await navigator.permissions.query({ name: 'camera' });
      const micPerm = await navigator.permissions.query({ name: 'microphone' });
      if (cameraPerm.state === 'denied' || micPerm.state === 'denied') {
        toast.error('Camera/microphone access blocked. Please allow permissions in your browser settings.');
        return false;
      }
      return true;
    } catch (err) {
      console.warn('Permission API not supported:', err);
      return true;
    }
  };

  // Desktop flow: create tracks via Agora's helpers
  const initAgoraDesktop = async (isReconnect = false) => {
    try {
      setLoading(true);
      setAgoraError('');

      const studentId = JSON.parse(localStorage.getItem('student'))?.id;
      if (!studentId) throw new Error('Student ID not found');

      if (isReconnect) {
        await cleanupAgora();
      }

      if (!clientRef.current || isReconnect) {
        clientRef.current = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
      }

      // Check if client is already connected
      if (
        clientRef.current.connectionState === 'CONNECTED' || 
        clientRef.current.connectionState === 'CONNECTING'
      ) {
        console.log('Client already connected/connecting, skipping join');
        setLoading(false);
        return;
      }

      const channelName = `exam${examId}`;
      const agoraUserId = Math.abs(Number(studentId)) % 65536;

      // Create camera and audio tracks only (NO SCREEN SHARE)
      if (proctoringStatus.camera && !localTracksRef.current.video) {
        localTracksRef.current.video = await AgoraRTC.createCameraVideoTrack();
      }
      if (proctoringStatus.microphone && !localTracksRef.current.audio) {
        localTracksRef.current.audio = await AgoraRTC.createMicrophoneAudioTrack();
      }

      await clientRef.current.join(APP_ID, channelName, null, agoraUserId);

      const publishTrack = async (track, type) => {
        if (!track) return;
        try {
          await clientRef.current.publish(track);
          setProctoringStatus(prev => ({ ...prev, [type]: true }));
          console.log(`Published ${type}`);
        } catch (err) {
          console.error(`Failed to publish ${type}:`, err);
          throw err;
        }
      };

      await publishTrack(localTracksRef.current.video, 'camera');
      await publishTrack(localTracksRef.current.audio, 'microphone');

      setLoading(false);
    } catch (error) {
      console.error('Agora desktop init failed:', error);
      setAgoraError(`Proctoring error: ${error.message}`);
      setLoading(false);
      throw error;
    }
  };

  // Mobile flow: request MediaStream via getUserMedia
  const requestLocalMedia = async ({ wantAudio = true, wantVideo = true }) => {
    try {
      const constraints = {
        video: wantVideo ? { facingMode: 'user' } : false,
        audio: wantAudio,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      return stream;
    } catch (err) {
      console.error('getUserMedia failed:', err);
      throw err;
    }
  };

  const createAgoraTracksFromStream = (stream, { publishAudio = true, publishVideo = true }) => {
    const created = { video: null, audio: null };

    if (publishVideo && stream.getVideoTracks().length > 0) {
      const videoTrack = stream.getVideoTracks()[0];
      created.video = AgoraRTC.createCustomVideoTrack({ mediaStreamTrack: videoTrack });
    }

    if (publishAudio && stream.getAudioTracks().length > 0) {
      const audioTrack = stream.getAudioTracks()[0];
      created.audio = AgoraRTC.createCustomAudioTrack({ mediaStreamTrack: audioTrack });
    }

    return created;
  };

  const initAgoraWithStream = async ({ 
    mediaStream, 
    publishAudio = true, 
    publishVideo = true, 
    isReconnect = false 
  }) => {
    try {
      setLoading(true);
      setAgoraError('');

      const studentId = JSON.parse(localStorage.getItem('student'))?.id;
      if (!studentId) throw new Error('Student ID not found');

      if (isReconnect) {
        await cleanupAgora();
      }

      if (!clientRef.current || isReconnect) {
        clientRef.current = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
      }

      const channelName = `exam${examId}`;
      const agoraUserId = Math.abs(Number(studentId)) % 65536;

      await clientRef.current.join(APP_ID, channelName, null, agoraUserId);

      const created = createAgoraTracksFromStream(mediaStream, { publishAudio, publishVideo });

      localTracksRef.current.video = created.video || null;
      localTracksRef.current.audio = created.audio || null;

      const publishTrack = async (track, type) => {
        if (!track) return;
        try {
          await clientRef.current.publish(track);
          setProctoringStatus(prev => ({ ...prev, [type]: true }));
          console.log(`Published ${type}`);
        } catch (err) {
          console.error(`Publish ${type} failed:`, err);
          throw err;
        }
      };

      await publishTrack(localTracksRef.current.video, 'camera');
      await publishTrack(localTracksRef.current.audio, 'microphone');

      setLoading(false);
    } catch (error) {
      console.error('Agora init with stream failed:', error);
      setAgoraError(`Proctoring error: ${error.message}`);
      setLoading(false);
      throw error;
    }
  };

  // Cleanup Agora and local tracks
  const cleanupAgora = async () => {
    try {
      if (clientRef.current) {
        try {
          await clientRef.current.unpublish();
        } catch (unpublishError) {
          console.warn('Error unpublishing tracks:', unpublishError);
        }

        try {
          await clientRef.current.leave();
        } catch (leaveError) {
          console.warn('Error leaving channel:', leaveError);
        }
      }

      // Stop/close local tracks (video and audio only)
      if (localTracksRef.current.video) {
        try {
          localTracksRef.current.video.stop && localTracksRef.current.video.stop();
          localTracksRef.current.video.close && localTracksRef.current.video.close();
        } catch (err) {
          console.warn('Error stopping video track:', err);
        }
      }
      if (localTracksRef.current.audio) {
        try {
          localTracksRef.current.audio.stop && localTracksRef.current.audio.stop();
          localTracksRef.current.audio.close && localTracksRef.current.audio.close();
        } catch (err) {
          console.warn('Error stopping audio track:', err);
        }
      }

      localTracksRef.current = { video: null, audio: null };
    } catch (error) {
      console.error('Cleanup error:', error);
    }
  };

  // Reconnection handler
  useEffect(() => {
    const handleConnectionStateChange = (state) => {
      if (state === 'DISCONNECTED') {
        console.log('Connection lost, trying to reconnect...');
        if (!isMobile) {
          initAgoraDesktop(true).catch(reconnectError => 
            console.error('Reconnection failed:', reconnectError)
          );
        }
      }
    };

    const client = clientRef.current;
    if (client) {
      client.on('connection-state-change', handleConnectionStateChange);
    }

    return () => {
      if (client) {
        client.off('connection-state-change', handleConnectionStateChange);
      }
    };
  }, [isMobile]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupAgora();
    };
  }, []);

  // Start proctoring (triggered by user gesture)
  const startProctoring = async () => {
    try {
      if (!(await checkPermissions())) throw new Error('Permissions denied');

      const wantAudio = !isMobile || (isMobile && true);

      if (isMobile) {
        // Mobile: use getUserMedia
        const stream = await requestLocalMedia({ wantAudio, wantVideo: true });

        // Show local preview
        if (cameraPreviewRef?.current) {
          try {
            cameraPreviewRef.current.srcObject = stream;
            cameraPreviewRef.current.play && cameraPreviewRef.current.play().catch(() => {});
          } catch (err) {
            console.warn('Preview start failed:', err);
          }
        }

        // Request fullscreen
        try {
          const elToFullscreen = document.documentElement;
          if (elToFullscreen.requestFullscreen) {
            await elToFullscreen.requestFullscreen();
          } else if (cameraPreviewRef?.current?.webkitEnterFullscreen) {
            cameraPreviewRef.current.webkitEnterFullscreen();
          }
        } catch (fsErr) {
          console.warn('Fullscreen request failed:', fsErr);
        }

        await initAgoraWithStream({ 
          mediaStream: stream, 
          publishAudio: wantAudio, 
          publishVideo: true 
        });

      } else {
        // Desktop flow: Update proctoringStatus before calling initAgoraDesktop
        await new Promise(resolve => {
          setProctoringStatus(prev => {
            const newStatus = {
              ...prev,
              camera: true,
              microphone: true,
            };
            // Call initAgoraDesktop after state update
            setTimeout(() => {
              initAgoraDesktop().then(resolve).catch(err => {
                console.error('Init error:', err);
                resolve();
              });
            }, 0);
            return newStatus;
          });
        });

        // Request fullscreen on desktop
        try {
          const elToFullscreen = document.documentElement;
          if (elToFullscreen.requestFullscreen) {
            await elToFullscreen.requestFullscreen();
          }
        } catch (err) {
          console.warn('Fullscreen failed:', err);
        }
      }

      // Move to exam attempt view
      setStep(3);
    } catch (err) {
      console.error('startProctoring error:', err);
      toast.error('Unable to access camera/microphone. Please allow permissions and try again.');
    }
  };

  // Handle next step navigation
  const handleNext = async () => {
    if (step === 2) {
      try {
        await startProctoring();
      } catch (err) {
        // Errors handled inside startProctoring
      }
    } else {
      setStep(prev => prev + 1);
    }
  };

  if (loading && !exam) {
    return <div>Loading exam details...</div>;
  }

  return (
    <div className={`p-${isMobile ? '2' : '3'}`}>
      {step === 1 && (
        <ExamSetup
          handleNext={handleNext}
          localTracksRef={localTracksRef}
          setProctoringStatus={setProctoringStatus}
          cameraPreviewRef={cameraPreviewRef}
          isMobile={isMobile}
        />
      )}

      {step === 2 && <ExamInstructions handleNext={handleNext} />}

      {step === 3 && exam && (
        <ExamAttempt
          exam={exam}
          attempt={attempt}
          proctoringStatus={proctoringStatus}
          setProctoringStatus={setProctoringStatus}
          videoTrack={localTracksRef.current.video}
          agoraError={agoraError}
        />
      )}
    </div>
  );
};

export default StartExam;